/**
 * 多言語表示用ヘルパー関数
 * 商品名、カテゴリ名、オプション名などの多言語フィールドを
 * 現在の言語設定に基づいて取得する
 */

type Language = 'ja' | 'en' | 'th';

interface MultiLangName {
  name: string;
  nameEn?: string;
  nameTh?: string;
  nameZhTw?: string;
  nameKo?: string;
}

interface MultiLangDescription {
  description?: string;
  descriptionEn?: string;
  descriptionTh?: string;
  descriptionZhTw?: string;
  descriptionKo?: string;
}

/**
 * 言語に応じた名前を取得
 * 該当言語がない場合は日本語名にフォールバック
 */
export function getLocalizedName(
  item: MultiLangName | null | undefined,
  language: Language
): string {
  if (!item) return '';

  switch (language) {
    case 'en':
      return item.nameEn || item.name || '';
    case 'th':
      return item.nameTh || item.name || '';
    default:
      return item.name || '';
  }
}

/**
 * 言語に応じた説明文を取得
 * 該当言語がない場合は日本語説明にフォールバック
 */
export function getLocalizedDescription(
  item: MultiLangDescription | null | undefined,
  language: Language
): string {
  if (!item) return '';

  switch (language) {
    case 'en':
      return item.descriptionEn || item.description || '';
    case 'th':
      return item.descriptionTh || item.description || '';
    default:
      return item.description || '';
  }
}

/**
 * 注文アイテム用の商品名取得
 * OrderItemはproductName, productNameEn, productNameThの形式
 */
interface OrderItemName {
  productName: string;
  productNameEn?: string;
  productNameTh?: string;
}

export function getLocalizedProductName(
  item: OrderItemName | null | undefined,
  language: Language
): string {
  if (!item) return '';

  switch (language) {
    case 'en':
      return item.productNameEn || item.productName || '';
    case 'th':
      return item.productNameTh || item.productName || '';
    default:
      return item.productName || '';
  }
}

/**
 * オプション選択肢用の名前取得
 */
interface OptionChoice {
  name: string;
  nameEn?: string;
  nameTh?: string;
  nameZhTw?: string;
  nameKo?: string;
}

export function getLocalizedChoiceName(
  choice: OptionChoice | null | undefined,
  language: Language
): string {
  if (!choice) return '';

  switch (language) {
    case 'en':
      return choice.nameEn || choice.name || '';
    case 'th':
      return choice.nameTh || choice.name || '';
    default:
      return choice.name || '';
  }
}

/**
 * カテゴリ配列から言語対応の名前マップを生成
 */
export function createLocalizedCategoryMap(
  categories: MultiLangName[],
  language: Language
): Map<string, string> {
  const map = new Map<string, string>();
  categories.forEach((cat: MultiLangName & { id?: string }) => {
    if (cat.id) {
      map.set(cat.id, getLocalizedName(cat, language));
    }
  });
  return map;
}
